import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
    setGuidesOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  const guideLinks = [
    { title: 'Kakobuy Guide 2026', slug: '/blog/kakobuy-spreadsheet-polska-guide-2026/' },
    { title: 'Jak Zacząć z Kakobuy', slug: '/blog/kakobuy-jak-zaczac/' },
    { title: 'Bezpieczeństwo', slug: '/blog/kakobuy-bezpieczenstwo/' },
    { title: 'Wysyłka do Polski', slug: '/blog/kakobuy-shipping-polska/' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <nav className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-shopping-bag-line text-sm" />
            </span>
            <span className="text-lg md:text-xl font-semibold font-heading text-foreground-950 whitespace-nowrap">
              Kakobuy Polska
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                isHome
                  ? 'text-primary-600 bg-primary-50/50'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('nav_home')}
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => { setDropdownOpen(!dropdownOpen); setGuidesOpen(false); }}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  dropdownOpen
                    ? 'text-primary-600 bg-primary-50/50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {t('nav_categories')}
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-500 overflow-hidden shrink-0">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      <span className="font-medium">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div ref={guidesRef} className="relative">
              <button
                onClick={() => { setGuidesOpen(!guidesOpen); setDropdownOpen(false); }}
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  guidesOpen
                    ? 'text-primary-600 bg-primary-50/50'
                    : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {t('nav_shopping_guide')}
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    guidesOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {guidesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 z-50">
                  {guideLinks.map((guide) => (
                    <Link
                      key={guide.slug}
                      to={guide.slug}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors"
                    >
                      <span className="w-6 h-6 flex items-center justify-center text-secondary-500">
                        <i className="ri-book-open-line" />
                      </span>
                      <span className="font-medium">{guide.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/blog/kakobuy-spreadsheet-polska-guide-2026/"
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                location.pathname.includes('/blog/')
                  ? 'text-primary-600'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
            >
              {t('nav_guides')}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2.5 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('cta_spreadsheet')}
            </a>
          </div>

          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 rounded-lg hover:bg-background-100 transition-colors"
            aria-label="Toggle menu"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </nav>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70 px-4 py-4 space-y-1 max-h-[75vh] overflow-y-auto">
          <Link
            to="/"
            className="block px-4 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100"
          >
            <i className="ri-home-4-line mr-2 text-foreground-500" />
            {t('nav_home')}
          </Link>

          <p className="px-4 pt-3 pb-1 text-xs font-semibold text-primary-600 uppercase tracking-wider flex items-center gap-1.5">
            <i className="ri-shopping-bag-line" />
            {t('nav_shopping_guide')}
          </p>
          {guideLinks.map((guide) => (
            <Link
              key={guide.slug}
              to={guide.slug}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100"
            >
              <i className="ri-book-open-line text-foreground-400" />
              {guide.title}
            </Link>
          ))}

          <p className="px-4 pt-3 pb-1 text-xs font-semibold text-foreground-500 uppercase tracking-wider flex items-center gap-1.5">
            <i className="ri-layout-grid-line" />
            {t('nav_categories')}
          </p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-2.5 text-xs text-foreground-700 rounded-lg hover:bg-background-100 transition-colors"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-500 overflow-hidden shrink-0">
                  <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                </span>
                <span className="font-medium leading-tight">{cat.name}</span>
              </a>
            ))}
          </div>

          <Link
            to="/blog/kakobuy-spreadsheet-polska-guide-2026/"
            className="block px-4 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 mt-2"
          >
            <i className="ri-book-open-line mr-2 text-foreground-500" />
            {t('nav_guides')}
          </Link>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block mt-3 px-4 py-3 text-sm font-semibold text-center bg-primary-500 text-background-50 rounded-lg"
          >
            {t('cta_spreadsheet')}
          </a>
        </div>
      )}
    </header>
  );
}